import { useNav } from "@pomle/react-router-paths";
import { ReactComponent as CompassIcon } from "assets/compass.svg";
import { paths } from "render/routes/paths";
import { FullScreenPageLayout } from "render/ui/layout/FullScreenPageLayout";
import { LogoHeader } from "render/ui/layout/LogoHeader";
import { Input } from "render/ui/presentation/Input";
import { Typography } from "render/ui/presentation/Typography";
import { ActionButton } from "render/ui/trigger/ActionButton";
import { ReactComponent as PlusIcon } from "./assets/plus-16x16.svg";

import { useCallback, useEffect, useState } from "react";

import { Skeleton } from "render/ui/presentation/Skeleton";
import { useCheckoutContext } from "../_CheckoutContext/CheckoutContext";
import styles from "./styles.module.sass";
import { Trans } from "./trans";

import { ReactComponent as CrossIcon } from "./assets/cross.svg";

import { PaymentRequestPaymentMethodEvent } from "@stripe/stripe-js";
import { ReactComponent as ChatIcon } from "assets/chat.svg";
import { useSnackbar } from "notistack";
import { useSession } from "render/context/MSALContext";
import { useTrackingContext } from "render/context/TrackingContext";
import { useContactUsPopup } from "render/hooks/popups/useContactUsPopup";
import { IconButton } from "render/ui/trigger/IconButton";
import { usePaymentRequestQuery } from "render/views/checkout/_CheckoutContext/hooks/usePaymentRequestQuery/usePaymentRequestQuery";
import { useWalletSubmitMutation } from "render/views/checkout/_CheckoutContext/hooks/useWalletSubmitMutation/useWalletSubmitMutation";
import { ProductPrice } from "render/views/checkout/components/ProductPrice";

function Country({ countryCode }: { countryCode: string }) {
  return (
    <>
      {countryCode === "SE" && <Trans.City.Stockholm />}
      {countryCode === "GB" && <Trans.City.London />}
    </>
  );
}

export function CodePayment() {
  const { isNewUser } = useSession();
  const { enqueueSnackbar } = useSnackbar();
  const { conversionsTracking } = useTrackingContext();
  const {
    applyDiscountCode,
    cart,
    fullPrice,
    discountCode,
    stripe,
    checkout,
    validateTokenMutation,
    updateCartMutation,
    discountApplied,
  } = useCheckoutContext();
  const contactUsPopup = useContactUsPopup();
  const nav = {
    home: useNav(paths.root),
    cardPayment: useNav(paths.checkoutCard),
  };

  const [formData, setFormData] = useState({
    code: discountCode,
  });

  const [formErrors, setFormError] = useState<{ discountCode: Error | null }>();

  useEffect(() => {
    setFormError({
      discountCode: validateTokenMutation.error ?? updateCartMutation.error,
    });
  }, [validateTokenMutation.error, updateCartMutation.error]);

  const [autoFocus] = useState(formData.code == null);

  const formControls = {
    reset: useCallback(() => {
      setFormError({ discountCode: null });
      setFormData((data) => {
        return { ...data, code: "" };
      });
    }, []),
    setCode: useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      setFormError({ discountCode: null });
      setFormData((data) => {
        return { ...data, code: e.target.value };
      });
    }, []),
    onSubmit: useCallback(
      (event?: React.FormEvent<HTMLFormElement>) => {
        event?.preventDefault();
        if (formData.code == null) {
          return;
        }
        setFormError({ discountCode: null });
        applyDiscountCode({ code: formData.code });
      },
      [applyDiscountCode, formData.code]
    ),
    errors: formErrors,
  };

  const walletCallbackMutation = useWalletSubmitMutation();
  const paymentRequestQuery = usePaymentRequestQuery({ cart, stripe });

  const walletCallbackMutationMutate = walletCallbackMutation.mutateAsync;

  useEffect(() => {
    if (
      paymentRequestQuery.data == null ||
      stripe == null ||
      checkout == null ||
      cart == null
    ) {
      return;
    }

    const { paymentRequest, wallet } = paymentRequestQuery.data;

    if (wallet?.applePay) {
      conversionsTracking.TrackWalletSupported("apple");
    }
    if (wallet?.googlePay) {
      conversionsTracking.TrackWalletSupported("google");
    }
    if (wallet?.applePay || wallet?.googlePay) {
      const handler = (ev: PaymentRequestPaymentMethodEvent) => {
        walletCallbackMutationMutate({ ev, stripe, checkout, cart }).catch(
          () => {
            enqueueSnackbar(<Trans.Error.Unexpected />, { variant: "error" });
          }
        );
      };
      paymentRequest.on("paymentmethod", handler);
      return () => {
        paymentRequest.off("paymentmethod", handler);
      };
    }
  }, [
    cart,
    conversionsTracking,
    paymentRequestQuery.data,
    stripe,
    checkout,
    walletCallbackMutationMutate,
    enqueueSnackbar,
  ]);

  const controls = {
    showAddCode: useCallback(() => {
      setFormData((data) => {
        return { ...data, code: "" };
      });
    }, []),
    payWithWallet: useCallback(() => {
      if (cart) {
        conversionsTracking.TrackBeginCheckout(cart);
      }
      if (cart && paymentRequestQuery.data?.wallet) {
        if (paymentRequestQuery.data?.wallet.applePay) {
          conversionsTracking.TrackAddPaymentInfo(cart, "apple");
        }
        if (paymentRequestQuery.data?.wallet.googlePay) {
          conversionsTracking.TrackAddPaymentInfo(cart, "google");
        }
      }
      paymentRequestQuery.data?.paymentRequest.show();
    }, [paymentRequestQuery.data, cart, conversionsTracking]),
    isPending: walletCallbackMutation.isPending,
  };

  return (
    <FullScreenPageLayout>
      <div className={styles.CodePaymentView}>
        <div className={styles.header}>
          <LogoHeader
            leftElement={
              isNewUser ? null : (
                <div className={styles.icon}>
                  <IconButton onClick={nav.home.on({})} icon={<CrossIcon />} />
                </div>
              )
            }
            rightElement={
              <IconButton
                onClick={contactUsPopup.emit}
                icon={
                  <div className={styles.icon}>
                    <ChatIcon />
                  </div>
                }
              />
            }
          />
        </div>
        <div className={styles.content}>
          <div className={styles.body}>
            <section className={styles.description}>
              <Typography variant="display-s">
                <Trans.Header />
              </Typography>
              <Typography variant="body-m" color="subtle">
                <Trans.Description />
              </Typography>
              <div className={styles.country}>
                {cart?.country ? (
                  <>
                    <CompassIcon />
                    <Typography variant="body-s">
                      {cart?.country && <Country countryCode={cart?.country} />}
                    </Typography>
                  </>
                ) : undefined}
              </div>
            </section>

            <section className={styles.product}>
              <ProductPrice
                discountApplied={discountApplied}
                cart={cart}
                discountCode={discountCode}
                fullPrice={fullPrice}
              />
            </section>

            <section className={styles.inviteCode}>
              {formData.code == null ? (
                <div className={styles.addCode}>
                  <Typography variant="body-m">
                    <Trans.Code.Label />
                  </Typography>
                  <button
                    disabled={paymentRequestQuery.data == null}
                    className={styles.addCodeButton}
                    onClick={controls.showAddCode}
                  >
                    <Typography
                      variant="eyebrow-m"
                      color={
                        paymentRequestQuery.data == null ? "subtle" : "default"
                      }
                    >
                      <Trans.CTA.AddCode />
                    </Typography>
                    <PlusIcon />
                  </button>
                </div>
              ) : null}
              {formData.code != null ? (
                <form onSubmit={formControls.onSubmit}>
                  <Input
                    disabled={discountApplied}
                    autoFocus={autoFocus}
                    autocomplete="off"
                    name="inviteCode"
                    label={<Trans.CodeForm.Label />}
                    error={
                      formControls.errors?.discountCode ? (
                        <Trans.Error.InvalidInvitationCode />
                      ) : undefined
                    }
                    hideClearButton
                    cta={
                      !discountApplied
                        ? {
                            label: <Trans.CodeForm.CTA />,
                            onClick: formControls.onSubmit,
                          }
                        : undefined
                    }
                    onChange={formControls.setCode}
                    onClear={formControls.reset}
                    value={formData.code}
                  />
                </form>
              ) : null}
            </section>

            <section
              className={styles.actions}
              data-loading={paymentRequestQuery.data == null}
            >
              {paymentRequestQuery.data == null ? (
                <>
                  <div className={styles.callToActionSkeleton}>
                    <Skeleton />
                  </div>
                </>
              ) : undefined}
              <div
                className={styles.buttons}
                data-loading={paymentRequestQuery.data == null}
              >
                {paymentRequestQuery.data != null ? (
                  <>
                    {paymentRequestQuery.data.wallet?.applePay ? (
                      <ActionButton
                        disabled={controls.isPending}
                        walletVariant="apple"
                        variant="wallet"
                        onClick={controls.payWithWallet}
                      >
                        <Trans.CTA.ApplePay />
                      </ActionButton>
                    ) : undefined}
                    {paymentRequestQuery.data.wallet?.googlePay ? (
                      <ActionButton
                        disabled={controls.isPending}
                        variant="wallet"
                        walletVariant="google"
                        onClick={controls.payWithWallet}
                      >
                        <Trans.CTA.GooglePay />
                      </ActionButton>
                    ) : undefined}
                    <ActionButton
                      disabled={controls.isPending}
                      variant="primary"
                      onClick={nav.cardPayment.on({})}
                    >
                      <Trans.CTA.CardPayment />
                    </ActionButton>
                  </>
                ) : undefined}
              </div>
            </section>
          </div>
        </div>
      </div>
    </FullScreenPageLayout>
  );
}
